import moment from 'moment';
import cs from 'classnames';
import { useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Space, Tooltip, Typography } from 'antd';
import { IssuesContext } from '../context/issues.context';
import { IIssue } from '../../../interfaces/issue.interface';
import Actions from './Actions';

export interface ColumnProps {
    title: string;
    dataIndex: string;
    key: string;
    align?: 'center' | 'left' | 'right';
    render?: (value: any, record: any) => React.ReactNode;
}

const { Text } = Typography;

const InitialColumns: () => ColumnProps[] = () => {
    const { setItem, fetchData, selectedColumns } = useContext(IssuesContext);
    const navigate = useNavigate();
    const location = useLocation();

    const OPTIONAL_COLUMNS = [
        {
            title: 'Workspace',
            dataIndex: 'workspaceName',
            key: 'workspaceName',
            align: 'center' as 'center',
            width: 320,
            render: (text: string, item: IIssue) => (
                <Link to={`/v2/workspaces/${item.workspaceID}`}>
                    <Space direction="vertical" align="center">
                        <Text style={{ color: '#177ddc' }}>
                            {text}
                        </Text>
                        <Text type="secondary">
                            {item?.workspaceUniqueID || ''}
                        </Text>
                    </Space>
                </Link>
            ),
        },
        {
            title: 'Payment Method',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            align: 'center' as 'center',
            render: (text: string) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            align: 'center' as 'center',
            render: (amount: number) => (
                <Text>
                    {`$ ${amount ? amount.toFixed(2) : Number(0).toFixed(2)}`}
                </Text>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            with: 320,
            render: (text: string) => (
                <Tooltip title={text}>
                    <Text
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {text.length > 30 ? `${text.slice(0, 30)}...` : text}
                    </Text>
                </Tooltip>
            ),
        },
    ];

    const INITIAL_COLUMNS = [
        {
            title: 'Issue ID',
            dataIndex: 'issuePublicID',
            key: 'issuePublicID',
            render: (text: string, record: IIssue) => (
                <Text
                    className="link"
                    onClick={() => {
                        setItem(record);
                        const searchParams = new URLSearchParams(location.search);
                        navigate(`/v2/issues?${searchParams.toString()}&open=${record.issuePublicID}`);
                    }}
                >
                    {text}
                </Text>
            ),
        },
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as 'center',
            render: (text: string) => {
                const date = moment(text).format('dddd, D MMM');
                const time = moment(text).format('h:mm a');
                return (
                    <Space size={4} direction="vertical">
                        <Text>
                            {date}
                        </Text>
                        <Text>
                            {time}
                        </Text>
                    </Space>
                );
            },
        },
        {
            title: 'Booking associated',
            dataIndex: 'bookingNumericId',
            key: 'bookingNumericId',
            align: 'center' as 'center',
            width: 180,
            render: (text: string, item: IIssue) => (
                <Link to={`/v2/booking?id=${item.bookingID}`}>
                    <Space direction="vertical">
                        <Text style={{ color: '#177ddc' }}>
                            {text}
                        </Text>
                    </Space>
                </Link>
            ),
        },
        {
            title: 'Issue Type',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as 'center',
            render: (text: string) => (
                <Text>
                    {text || 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as 'center',
            width: 180,
            render: (text: string) => (
                <Text className={cs(text, 'status')}>
                    {text}
                </Text>
            ),
        },
        {
            title: 'Actions',
            dataIndex: '_id',
            key: 'actions',
            align: 'center' as 'center',
            render: (_id: string, record: IIssue) => (
                <Actions
                    item={record}
                    setItem={setItem}
                    fetchData={fetchData}
                />
            ),
        },
    ];

    const COLUMNS = useMemo(() => {
        const columnOrder = [
            'issuePublicID',
            'createdAt',
            'bookingNumericId',
            'workspaceName',
            'type',
            'status',
            'paymentMethod',
            'amount',
            'description',
            'actions',
        ];
        const columns = [
            ...INITIAL_COLUMNS,
            ...OPTIONAL_COLUMNS.filter((column) => selectedColumns.includes(column.key)),
        ];
        const columnMap = Object.fromEntries(columns.map((column) => [column.key, column]));
        return columnOrder.map((key) => columnMap[key]).filter(Boolean);
    }, [selectedColumns]);

    return COLUMNS as ColumnProps[];
};

export default InitialColumns;
